import { OktaAuth } from '@okta/okta-auth-js';
import { devtoolsExchange } from '@urql/devtools';
import { authExchange } from '@urql/exchange-auth';
import { cacheExchange } from '@urql/exchange-graphcache';
import { retryExchange } from '@urql/exchange-retry';
import { feedback } from 'react-feedbacker';
import {
  CombinedError,
  Operation,
  createClient,
  errorExchange,
  fetchExchange,
} from 'urql';

import {
  Agent,
  BonusTransactionData,
  Brand,
  ChallengeItemV3,
  Customer,
  CustomerRiskAssessment,
  GameDescriptor,
  GameDescriptorV2,
  Payment,
  PendingKycFlowViewItem,
  PlayerSearchItem,
  RewardItem,
  RgCraAutomationLegacyHistoryItem,
  Risk,
  TransactionSearchItem,
  Wallet,
} from '@/globalTypes';
import { logger } from '@/utils';
import schema from './../backoffice-schema.json';
import { urqlMutationUpdates } from './urqlMutationUpdates';

// Token renewal configuration constants
const TOKEN_RENEWAL_CHECK_INTERVAL_MS = 60_000; // Check every 60 seconds
const TOKEN_EXPIRY_THRESHOLD_SECONDS = 300; // Renew if expires within 5 minutes
const RENEWAL_RETRY_DELAY_MS = 1000; // Wait time when renewal is in progress
const ERROR_DISPLAY_THROTTLE_MS = 10_000; // Show same error once per 10 seconds

// Track last displayed error to avoid spam
let lastError = { text: '', time: 0 };

const hasInvalidOktaToken = (error: CombinedError) => {
  return error.graphQLErrors.some(
    (gqlError) =>
      // @ts-expect-error errorCode does exist on originalError
      gqlError.originalError?.errorCode === 'INVALID_OKTA_TOKEN',
  );
};

/**
 * Formats error messages with context-specific information
 */
const getErrorMessage = (error: CombinedError): string => {
  let message = error.message;

  if (message === '[Network] Failed to fetch') {
    message += `. Are you connected to the VPN?`;
  }

  if (hasInvalidOktaToken(error)) {
    message = `Your session has expired. Attempting to refresh your token...`;
  }

  return message;
};

/**
 * Shows error feedback, throttled to avoid displaying the same message repeatedly
 */
const showErrorOnce = (message: string): void => {
  const now = Date.now();

  if (
    lastError.text !== message ||
    now - lastError.time > ERROR_DISPLAY_THROTTLE_MS
  ) {
    lastError = { text: message, time: now };
    feedback.error(message);
  }
};

const initializeAuthState = async (oktaAuth: OktaAuth) => {
  const tokens = await oktaAuth.tokenManager.getTokens();
  const token = tokens.accessToken?.accessToken;
  const refreshToken = tokens.refreshToken?.refreshToken;
  const isAuthenticated =
    await oktaAuth.authStateManager.getAuthState()?.isAuthenticated;

  return {
    token,
    refreshToken,
    isAuthenticated,
  };
};

export const makeClient = (url: string, oktaAuth: OktaAuth) => {
  const cacheConfig = {
    schema,
    // prettier-ignore
    keys: {
      AccountStatus: () => null,
      AdjustmentTypeData: () => null,
      AffiliateInfo: () => null,
      Agent: ({ agentId }: Agent) => agentId,
      AgentInitiator: () => null,
      AllLinkedAccounts: () => null,
      AmlRiskAssessmentReport: () => null,
      AuthenticationMethod: () => null,
      BoSourceOfFundType: () => null,
      BoSourceOfFundsType: () => null,
      Bonus: () => null,
      BonusMoneyDropChallengeReward: () => null,
      BonusMoneyDropReward: () => null,
      BonusMoneyTemplate: () => null,
      BonusTransactionData: ({ bonusId }: BonusTransactionData) => bonusId,
      Brand: ({ code }: Brand) => code,
      CalculatedReward: () => null,
      CashbackTransactionItem: () => null,
      ChallengeItemV3: ({ challengeId }: ChallengeItemV3) => challengeId,
      ChallengeRewardV2: () => null,
      ComplianceGlobalPlayerLimit: () => null,
      CraRiskLevelChangedItem: () => null,
      CustomerClosure: () => null,
      CustomerRestriction: () => null,
      CustomerRiskAssessment: ({ playerGlobalId }: CustomerRiskAssessment) => playerGlobalId,
      CustomerRiskAssessmentHistoryItem: () => null,
      DepositChallengeCriteria: () => null,
      DepositInfo: () => null,
      DinoOptInHistoryEntry: () => null,
      DocumentResponse: () => null,
      DynamicBonusMoneyDropChallengeReward: () => null,
      DynamicDepositAmount: () => null,
      DynamicFreeSpinChallengeReward: () => null,
      DynamicMoneyDropChallengeReward: () => null,
      EmailInfo: () => null,
      Fee: () => null,
      FinalRiskLevelUpdated: () => null,
      FreeSpinChallengeReward: () => null,
      FreeSpinReward: () => null,
      FreespinsTemplate: () => null,
      FundingDocumentsRequestedItem: () => null,
      GameDescriptor: (data: GameDescriptor) => data?.json?.gameId,
      GameDescriptorV2: (data: GameDescriptorV2) => data?.json?.gameId,
      GameRoundItem: () => null,
      GlobalLoginAttemptsInfo: () => null,
      GlobalOrigin: () => null,
      GlobalWallet: ({ playerGlobalId }: Customer) => playerGlobalId,
      HampiCheckResult: () => null,
      Identity: () => null,
      InstantBankingBlock: () => null,
      KYC6AggregatedMatches: () => null,
      KYC6Match: () => null,
      KYC6MatchReport: () => null,
      KycCheckStatus: () => null,
      KycFlow: () => null,
      Limit: () => null,
      LimitSearchItem: () => null,
      LinkedAccounts: () => null,
      LoginInfo: () => null,
      MarketingPreferences: () => null,
      Money: () => null,
      MoneyDropChallengeReward: () => null,
      MoneyDropReward: () => null,
      MoneyTemplate: () => null,
      NoteResponse: () => null,
      OverwritableStatusCheck: () => null,
      Overwrite: () => null,
      Payment: ({ rawPaymentId }: Payment) => rawPaymentId,
      PaymentApproval: () => null,
      PendingKycFlowViewItem: ({ flowId }: PendingKycFlowViewItem) => flowId,
      PendingLimit: () => null,
      PeriodLimit: () => null,
      PlayerAddressInfo: () => null,
      PlayerCalculatedReward: () => null,
      PlayerComplianceDetails: () => null,
      PlayerComplianceGroupedV2: () => null,
      PlayerExportDetails: () => null,
      PlayerGamificationBlockStatus: () => null,
      PlayerGamificationDetails: () => null,
      PlayerIdPerBrand: () => null,
      PlayerInfoHistory: () => null,
      PlayerInitiator: () => null,
      PlayerKYCValidationItem: () => null,
      PlayerKYCVerifiedItem: () => null,
      PlayerLoginInfo: () => null,
      PlayerLoginInfoData: () => null,
      PlayerRewardFlatItem: () => null,
      PlayerSearchItem: ({ playerId }: PlayerSearchItem) => playerId,
      PlayerStats: () => null,
      Region: () => null,
      RegistrationInfo: () => null,
      RequestedFundingDocument: () => null,
      ReviewStatusInfo: () => null,
      ReviewTrigger: () => null,
      RewardItem: ({ rewardId }: RewardItem) => rewardId,
      RgCraAutomationHistoryItem: () => null,
      RgCraAutomationLegacyHistoryItem: ({ eventId }: RgCraAutomationLegacyHistoryItem) => eventId,
      RgCraRegion: () => null,
      RgCraReviewStatusInfo: () => null,
      RgCraReviewTrigger: () => null,
      RgCraRisk: () => null,
      RgCraV2CustomerRiskAssessmentHistoryItem: () => null,
      RgCraV2FinalRiskLevelUpdated: () => null,
      RgCraV2GamingCustomerRiskAssessment: () => null,
      RgCraV2Region: () => null,
      RgCraV2ReviewApproved: () => null,
      RgCraV2ReviewNeeded: () => null,
      RgCraV2ReviewNotNeeded: () => null,
      RgCraV2ReviewPending: () => null,
      RgCraV2ReviewRejected: () => null,
      RgCraV2ReviewStatusInfo: () => null,
      RgCraV2ReviewTrigger: () => null,
      RgCraV2Risk: ({ riskId }: Risk) => riskId,
      RgCraV2RiskLevelUpdated: () => null,
      RgCraV2RiskOverrideAdded: () => null,
      RgCraV2RiskOverrideRemoved: () => null,
      RgCraV2RiskPath: () => null,
      RgCraV2RiskRemoved: () => null,
      Risk: ({ riskId }: Risk) => riskId,
      RiskLevelUpdated: () => null,
      RiskOverrideAdded: () => null,
      RiskOverrideRemoved: () => null,
      RollingPeriod: () => null,
      ScreeningStatuses: () => null,
      SelfExclusionData: () => null,
      SessionSearchItem: () => null,
      SourceOfWealth: () => null,
      SourceOfWealthManuallyRequested: () => null,
      SourceOfWealthRequiredItem: () => null,
      SourceOfWealthSubmittedItem: () => null,
      SourceOfWealthVerifiedItem: () => null,
      SowExportResponse: () => null,
      StatsEntryType: () => null,
      StatsType: () => null,
      StatusCheck: () => null,
      TransactionExportDetails: () => null,
      TransactionSearchItem: ({ uuid }: TransactionSearchItem) => uuid,
      TranslatedPersonalInfo: () => null,
      VerificationWithProvider: () => null,
      Viewer: () => null,
      WageringRequirementSummary: () => null,
      Wallet: ({ playerId }: Wallet) => playerId,
    },
    updates: {
      Mutation: urqlMutationUpdates,
    },
  };

  const ourAuthExchange = authExchange(async (utils) => {
    let { token } = await initializeAuthState(oktaAuth);
    let lastRenewalCheck = 0;
    let isRenewing = false;

    const checkAndRenewToken = async () => {
      const now = Date.now();

      // Throttle: only check every 60 seconds
      if (now - lastRenewalCheck < TOKEN_RENEWAL_CHECK_INTERVAL_MS) {
        return;
      }

      // Prevent concurrent renewal attempts
      if (isRenewing) {
        return;
      }

      lastRenewalCheck = now;

      const tokens = await oktaAuth.tokenManager.getTokens();
      const accessToken = tokens.accessToken;

      if (!accessToken) {
        return;
      }

      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = accessToken.expiresAt - currentTime;

      // Renew if token expires within threshold (5 minutes)
      if (timeUntilExpiry < TOKEN_EXPIRY_THRESHOLD_SECONDS) {
        isRenewing = true;

        logger.log(
          `Token expiring in ${timeUntilExpiry}s, renewing proactively...`,
        );

        try {
          await oktaAuth.tokenManager.renew('accessToken');
          const renewedTokens = await oktaAuth.tokenManager.getTokens();
          token = renewedTokens.accessToken?.accessToken;
          logger.log('Token renewed proactively');
        } catch (error) {
          logger.log('Proactive token renewal failed:', error);
          // Continue with existing token; will trigger refreshAuth on error
        } finally {
          isRenewing = false;
        }
      } else {
        // Keep token in sync with token manager
        token = accessToken.accessToken;
      }
    };

    return {
      addAuthToOperation: (operation) => {
        // Non-blocking token renewal check
        checkAndRenewToken().catch((err) => {
          logger.log('Token renewal check failed:', err);
        });

        if (token) {
          return utils.appendHeaders(operation, {
            Authorization: `Bearer ${token}`,
          });
        }

        return operation;
      },
      willAuthError: () => !token,
      didAuthError: (error) => hasInvalidOktaToken(error),
      refreshAuth: async () => {
        // If renewal already in progress, wait and retrieve the renewed token
        if (isRenewing) {
          logger.log('Token renewal already in progress, waiting...');

          await new Promise((resolve) =>
            setTimeout(resolve, RENEWAL_RETRY_DELAY_MS),
          );

          const tokens = await oktaAuth.tokenManager.getTokens();
          token = tokens.accessToken?.accessToken;
          return;
        }

        isRenewing = true;

        try {
          await oktaAuth.tokenManager.renew('accessToken');
          const tokens = await oktaAuth.tokenManager.getTokens();
          token = tokens.accessToken?.accessToken;
          logger.log('Token refreshed successfully');
        } catch (error) {
          logger.log('Token refresh failed:', error);
          // If refresh fails, redirect to login
          await oktaAuth.signInWithRedirect();
        } finally {
          isRenewing = false;
        }
      },
    };
  });

  // Maximum number of retries will be 1 with an initial delay of 3 sec between first request and retry
  const retryOptions = {
    initialDelayMs: 3000,
    maxNumberAttempts: 1,
  };

  return createClient({
    url,
    fetchOptions: {
      headers: {
        accept: 'application/json',
        operator: process.env.GATSBY_OPERATOR!,
      },
    },
    exchanges: [
      errorExchange({
        onError: (error: CombinedError, operation: Operation) => {
          const message = getErrorMessage(error);
          showErrorOnce(message);
          logger.log({ error, operation });
        },
      }),
      devtoolsExchange,
      cacheExchange(cacheConfig),
      ourAuthExchange,
      retryExchange(retryOptions),
      fetchExchange,
    ],
  });
};
